import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

export type AdminNotifType = 'ordine' | 'cliente' | 'rma' | 'stock' | 'erp' | 'sistema' | 'difformita' | 'contatto';

export interface AdminNotification {
  id: string;
  type: AdminNotifType;
  title: string;
  body: string;
  read: boolean;
  readAt: string | null;
  createdAt: string;
  actionLink: string | null;
  meta: Record<string, unknown>;
}

export interface AdminNotificationsResp {
  items: AdminNotification[];
  total: number;
  unreadCount: number;
}

interface AdminNotificationsContextValue {
  data: AdminNotificationsResp | null;
  loading: boolean;
  reload: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (id: string) => Promise<void>;
}

const AdminNotificationsContext = createContext<AdminNotificationsContextValue | null>(null);

/**
 * Provider per le notifiche del backoffice admin.
 * Flusso separato dalle notifiche cliente.
 * Effettua il fetch solo quando l'utente ha ruolo "admin".
 */
export function AdminNotificationsProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = isAuthenticated && user?.ruolo === 'admin';

  const [data, setData] = useState<AdminNotificationsResp | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<AdminNotificationsResp>(
        '/api/admin/notifications?limit=100'
      );
      setData(data);
    } catch {
      // Fail silenziosamente — l'admin vedrà 0 notifiche
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      reload();
    } else {
      setData(null);
    }
  }, [isAdmin, reload]);

  const markRead = useCallback(async (id: string) => {
    await api.patch(`/api/admin/notifications/${id}/read`);
    await reload();
  }, [reload]);

  const markAllRead = useCallback(async () => {
    await api.patch('/api/admin/notifications/read-all');
    await reload();
  }, [reload]);

  const remove = useCallback(async (id: string) => {
    await api.delete(`/api/admin/notifications/${id}`);
    await reload();
  }, [reload]);

  return (
    <AdminNotificationsContext.Provider value={{ data, loading, reload, markRead, markAllRead, remove }}>
      {children}
    </AdminNotificationsContext.Provider>
  );
}

export function useAdminNotifications(): AdminNotificationsContextValue {
  const ctx = useContext(AdminNotificationsContext);
  if (!ctx) throw new Error('useAdminNotifications must be used within <AdminNotificationsProvider>');
  return ctx;
}

export function useAdminNotificationsOptional(): AdminNotificationsContextValue | null {
  return useContext(AdminNotificationsContext);
}
